import { isEmbeddedPage } from 'config/eva';
import { mixpanel } from 'utils/trackers/mixpanel';

import { pushConsentToDataLayer } from './pushConsentToDataLayer';
import type { OsanoConsent } from './types';

function isCmpUiSuppressed(): boolean {
  return Boolean(window.isPwnHealthPage) || isEmbeddedPage();
}

function getOsano() {
  return window.Osano;
}

function readConsent(): OsanoConsent | undefined {
  try {
    return getOsano()?.cm?.getConsent?.();
  } catch {
    return undefined;
  }
}

function applyConsentFromCmp() {
  if (isCmpUiSuppressed()) {
    return;
  }

  const consent = readConsent();
  const analyticsOn = consent?.ANALYTICS === 'ACCEPT';

  mixpanel.syncTrackingConsentFromOsano(analyticsOn);
  pushConsentToDataLayer(consent);
}

let started = false;

/**
 * Wire Osano consent to Mixpanel opt status + dataLayer (GTM / Consent Mode).
 * Call once from `gatsby-browser` after `mixpanel.init()`.
 */
export function bootstrapOsanoIntegrations() {
  if (typeof window === 'undefined' || started) {
    return;
  }
  started = true;

  if (isCmpUiSuppressed()) {
    mixpanel.syncTrackingConsentFromOsano(false);
    return;
  }

  const Osano = getOsano();
  if (!Osano) {
    // Stub queue is created synchronously by gatsby-ssr.jsx; if it's missing
    // here, the Osano script was likely blocked or removed. Surface it so we
    // can spot lost consent sync in Sentry/console logs rather than silently
    // leaving Mixpanel opted out and GTM stuck on deny defaults.
    // eslint-disable-next-line no-console
    console.warn(
      '[osano] CMP queue not found on window; consent sync disabled.',
    );
    return;
  }

  const run = () => {
    if (!getOsano()?.cm?.getConsent) {
      return;
    }
    applyConsentFromCmp();
  };

  Osano('onInitialized', run);
  Osano('onConsentSaved', run);

  run();
}

/**
 * Reset the module-level singleton so each test starts from a clean slate.
 * Not part of the production API.
 */
export function __resetForTests() {
  started = false;
}
